import { format } from 'date-fns';
import { formatMoney } from './money';

// Money helpers for splitting expenses. Amounts are handled in paise so the
// shares always add up to the exact total.

export const normalizeEmail = (email) => (typeof email === 'string' ? email.trim().toLowerCase() : '');

export const toPaise = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num * 100) : NaN;
};

export const fromPaise = (paise) => paise / 100;

// Equal shares; the leftover paise go to the first people in the list
export const splitEqually = (amount, emails) => {
  const amountPaise = toPaise(amount);
  if (!Number.isFinite(amountPaise) || emails.length === 0) return [];
  const base = Math.floor(amountPaise / emails.length);
  const remainder = amountPaise - base * emails.length;
  return emails.map((email, i) => ({
    email,
    shareAmount: fromPaise(base + (i < remainder ? 1 : 0))
  }));
};

// Calendar date as YYYY-MM-DD in the user's own timezone
// (toISOString() would shift it to UTC and lose a day in India)
export const formatDateForApi = (date) => format(date, 'yyyy-MM-dd');

/**
 * People taking part in the split. The current user is always included.
 * @returns {Array<{ email: string, name: string|null, isYou: boolean }>}
 */
export const getParticipants = ({ splitType, groupId, selectedFriends }, groups, me) => {
  const meEmail = normalizeEmail(me?.email);
  let people = [];
  if (splitType === 'group') {
    const group = groups.find(g => g.id === groupId);
    people = group ? group.members : [];
  } else {
    people = selectedFriends;
  }

  const byEmail = new Map();
  if (meEmail) {
    byEmail.set(meEmail, { email: meEmail, name: me.name || null, isYou: true });
  }
  for (const person of people) {
    const email = normalizeEmail(person.email);
    if (email && !byEmail.has(email)) {
      byEmail.set(email, { email, name: person.name || null, isYou: false });
    }
  }
  return [...byEmail.values()];
};

/**
 * Build the splits payload for the API.
 * @returns {{ splits: Array<{ email: string, shareAmount: number }> } | { error: string }}
 */
export const buildSplits = ({ amount, participants, splitMethod, customSplits }) => {
  const amountPaise = toPaise(amount);
  if (!Number.isFinite(amountPaise) || amountPaise <= 0) {
    return { error: 'Enter an amount greater than zero' };
  }
  if (participants.length < 2) {
    return { error: 'Pick at least one other person to split with' };
  }

  if (splitMethod === 'equal') {
    return { splits: splitEqually(fromPaise(amountPaise), participants.map(p => p.email)) };
  }

  const splits = [];
  for (const participant of participants) {
    const raw = customSplits[participant.email];
    const sharePaise = raw === undefined || raw === '' ? 0 : toPaise(raw);
    if (!Number.isFinite(sharePaise) || sharePaise < 0) {
      return { error: `Enter a valid amount for ${participant.name || participant.email}` };
    }
    splits.push({ email: participant.email, shareAmount: fromPaise(sharePaise) });
  }

  const totalPaise = splits.reduce((sum, s) => sum + toPaise(s.shareAmount), 0);
  if (totalPaise !== amountPaise) {
    const diff = fromPaise(amountPaise - totalPaise);
    return {
      error: diff > 0
        ? `${formatMoney(diff)} is still unassigned`
        : `Shares are ${formatMoney(Math.abs(diff))} more than the total`
    };
  }
  return { splits };
};

// Remaining amount for unequal splits (can be negative when over-assigned)
export const remainingAmount = ({ amount, participants, customSplits }) => {
  const amountPaise = toPaise(amount);
  if (!Number.isFinite(amountPaise)) return 0;
  const assigned = participants.reduce((sum, p) => {
    const share = toPaise(customSplits[p.email]);
    return sum + (Number.isFinite(share) ? share : 0);
  }, 0);
  return fromPaise(amountPaise - assigned);
};

// 'YYYY-MM-DD' from the API as a local calendar date
export const parseApiDate = (value) => {
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Edit-form state for an existing shared expense (as returned by the API).
 * Shares that are all equal (within a paisa) open as "Split Equally".
 */
export const formFromExpense = (expense) => {
  const others = expense.splits.filter(s => !s.isYou);
  const includesYou = expense.splits.some(s => s.isYou);
  const shares = expense.splits.map(s => toPaise(s.shareAmount));
  const isEqual = includesYou && Math.max(...shares) - Math.min(...shares) <= 1;

  return {
    description: expense.description,
    amount: String(expense.amount),
    expenseDate: parseApiDate(expense.expenseDate),
    category: expense.category || '',
    paidByEmail: normalizeEmail(expense.paidBy.email),
    splitType: 'friends',
    groupId: '',
    selectedFriends: others.map(s => ({ id: s.email, email: normalizeEmail(s.email), name: s.name })),
    splitMethod: isEqual ? 'equal' : 'unequal',
    customSplits: Object.fromEntries(expense.splits.map(s => [normalizeEmail(s.email), String(s.shareAmount)]))
  };
};
